"use client";

import { Handle, type NodeProps, Position } from "@xyflow/react";
import { Sparkles } from "lucide-react";
import { memo } from "react";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useDirection } from "../DirectionContext";
import type { CheckInfo, SummaryNodeData } from "../types";
import { AdminNodeActions } from "./shared/AdminNodeActions";
import { ElapsedTime } from "./shared/ElapsedTime";
import { NodeFrame } from "./shared/NodeFrame";
import { ProgressBar } from "./shared/ProgressBar";
import {
  calcBarPercent,
  formatProgressMetrics,
} from "./shared/progress-metrics";
import { StatusBadge } from "./shared/StatusBadge";
import { STATUS_COLORS } from "./shared/status-styles";

function CheckRow({ check }: { check: CheckInfo }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px]">
      <StatusBadge status={check.status} />
      <span className="text-gray-600 whitespace-nowrap">{check.name}</span>
    </div>
  );
}

export const SummaryNode = memo(function SummaryNode({ id, data }: NodeProps) {
  const d = data as SummaryNodeData;
  const dir = useDirection();
  const adminStatus = useIsAdmin();
  const checks = d.checks ?? [];
  const barPct = d.contentLength ? calcBarPercent(d.contentLength) : 0;
  const colors = STATUS_COLORS[d.status];
  const isActive = d.status === "active";
  const isReceiving =
    (d.tokensReceived ?? 0) > 0 ||
    (d.bytesReceived ?? 0) > 0 ||
    (d.contentLength ?? 0) > 0;

  let stateClass = "";
  if (d.status === "pending") {
    stateClass = "!bg-amber-50 !border-l-amber-200";
  } else if (isActive && isReceiving) {
    stateClass =
      "pipeline-node-active-streaming !bg-blue-50 !border-l-blue-400";
  } else if (isActive) {
    stateClass =
      "pipeline-node-active-waiting !bg-amber-100 !border-l-amber-400";
  }

  return (
    <NodeFrame
      status={d.status}
      className={`min-w-[200px] w-max max-w-[360px] ${stateClass}`}
    >
      <Handle
        type="target"
        position={dir === "LR" ? Position.Left : Position.Top}
        className="!bg-transparent !border-0 !w-0 !h-0"
      />
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles size={14} className={`shrink-0 ${colors.icon}`} />
          <span
            className={`text-xs font-medium ${colors.text} whitespace-nowrap`}
          >
            {d.label}
          </span>
        </div>
        <ElapsedTime ms={d.elapsedMs} />
      </div>
      {checks.length > 0 && (
        <div className="border-t border-gray-100 pt-1 mt-1 flex flex-col gap-0.5">
          {checks.map((c) => (
            <CheckRow key={c.name} check={c} />
          ))}
        </div>
      )}
      <div className="mt-1.5">
        <ProgressBar percent={barPct} status={d.status} />
        <div
          className={`text-[10px] mt-0.5 tabular-nums ${d.status === "completed" ? "text-amber-500" : "text-gray-400"}`}
        >
          {formatProgressMetrics(
            d.tokensReceived,
            d.contentLength,
            d.bytesReceived,
          )}
        </div>
      </div>
      {adminStatus === "admin" && (
        <AdminNodeActions
          nodeId={d.nodeId ?? id}
          nodeStatus={d.status}
          nodeExecutionDirectives={d.nodeExecutionDirectives}
          onAdminAction={d.onAdminAction}
        />
      )}
      <Handle
        type="source"
        position={dir === "LR" ? Position.Right : Position.Bottom}
        className="!bg-transparent !border-0 !w-0 !h-0"
      />
    </NodeFrame>
  );
});
